import React, { useEffect, useRef, forwardRef } from 'react';

interface AutoFocusInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  focusDelay?: number; // Optional delay before focusing (useful for animations)
}

const AutoFocusInput = forwardRef<HTMLInputElement, AutoFocusInputProps>((props, ref) => {
  const { disabled, focusDelay = 0, ...rest } = props;
  const localRef = useRef<HTMLInputElement>(null);
  const inputRef = (ref as React.RefObject<HTMLInputElement>) || localRef;
  const focusTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Clear any existing timeout
    if (focusTimeoutRef.current) {
      clearTimeout(focusTimeoutRef.current);
    }

    // Focus on mount if not disabled
    if (!disabled) {
      focusTimeoutRef.current = setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          // Ensure the element is visible and scroll into view if needed
          inputRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      }, focusDelay);
    }

    // Cleanup timeout on unmount
    return () => {
      if (focusTimeoutRef.current) {
        clearTimeout(focusTimeoutRef.current);
      }
    };
  }, [disabled, focusDelay, inputRef]);

  return <input ref={inputRef} disabled={disabled} {...rest} />;
});

AutoFocusInput.displayName = 'AutoFocusInput';

export default AutoFocusInput; 